<template>
    <standard-dialog v-model='dialog' :max-width="600" persistent>
        <template v-slot:title>
            Select {{ locationName }}
            <v-spacer></v-spacer>
            <v-btn @click="onAddNewLocation" small>Add New</v-btn>
        </template>
        <template v-slot:content>
            <v-form ref="form" :lazy-validation="true">
                <v-container fluid v-if="locations">
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                :items="filteredLocations"
                                item-value="id"
                                item-text="name"
                                :rules="[$rules.required()]"
                                :label="locationName"
                                v-model="selectedLocations"
                                :menu-props="{ dark: true, contentClass:'with-prepend', maxHeight:'600' }"
                                multiple
                                ref="locations"
                            >
                                <template v-slot:item="{ item, attrs }">
                                    <v-icon class="mr-7">
                                        {{ attrs.inputValue ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                                    </v-icon>
                                    <v-chip small class="mr-2">{{ item.displayOrder }}</v-chip>
                                    {{ item.name }}
                                </template>
                                <template v-slot:prepend-item>
                                    <div class="prepend theme--dark v-sheet">
                                        <v-list-item ripple>
                                            <v-list-item-content class="pa-0">
                                                <v-list-item-title class="d-flex">
                                                    <v-text-field outlined dense hide-details class="mt-1" label="Search" v-model="locationFilter" clearable />
                                                    <v-spacer></v-spacer>
                                                    <v-btn text small @click="$refs['locations'].blur()"> <v-icon left>mdi-close</v-icon>Close </v-btn>
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="my-2"></v-divider>
                                        <v-list-item ripple @click="toggle">
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    <v-icon class="mr-7">{{ icon }}</v-icon
                                                    >Select All
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mt-2"></v-divider>
                                    </div>
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>
        </template>
        <template v-slot:actions>
            <v-spacer></v-spacer>
            <v-btn color="gray" text @click="dialog = false">Cancel</v-btn>
            <v-access-control>
                <template #offline>
                    <offline-btn text></offline-btn>
                </template>
                <v-btn color="primary" text @click="select()" :loading="$wait.is('saving')"
                >Select</v-btn
                >
            </v-access-control>
        </template>
        <template v-slot:sub-dialogs>
            <location-creation-wizard
                :location-name-convention='locationNameConvention'
                :available-default-cycles='filteredCycles'
                :available-reduced-levels='filteredReducedLevels'
                :initial-information='newLocation'
                :archived-location-names='[]'
                :available-parent-stopes="filteredOpenStopes"
                v-if='newLocation'
                @save="saveAndAddNewLocation"
                @save-child-location="saveAndAddNewStopeChild"
                @cancel="cancelAddNewLocation"
            >
            </location-creation-wizard>
        </template>
    </standard-dialog>
</template>
<script>
import Locations from '@/lib/data/Locations';
import MineAreaStore, { useMineAreaStore } from '@/lib/stores/MineAreaStore';
import ReducedLevels from '@/lib/data/ReducedLevels';
import Cycles from '@/lib/data/Cycles';
import { CycleType } from '@/models/api/Enums/cycle-type';
import { waitFor } from 'vue-wait';
import { enumOptions } from '@/lib/EnumExtensions';
import LocationCreationWizard from '@/components/Shared/Locations/LocationCreationWizard.vue';
import { LocationStatus, MiningMethodType } from '@/models/api';
import { mapState } from 'pinia';
import { EventBus, Events } from '@/lib/EventBus';

export default {
    components: { LocationCreationWizard },
    props: ['locationName', 'mineAreaId', 'departmentId'],
    data() {
        return {
            selectedLocations: [],
            locations: null,
            selectedDepartment: null,
            dialog: false,
            existing: [],
            newLocation: null,
            reducedLevels: [],
            cycles: [],
            cycleTypes: enumOptions(CycleType, this.$options.filters.cycleType),
            locationFilter: '',
            CycleType
        };
    },
    async created() {
        for (let mine of MineAreaStore.mineAreas) {
            for (let dept of mine.departments) {
                if (dept.id == this.departmentId) {
                    this.selectedDepartment = dept;
                    break;
                }
            }
            if (this.selectedDepartment) break;
        }
    },
    methods: {
        async show(existing) {
            this.existing = existing;
            this.dialog = true;
            this.selectedLocations = [];
            await this.loadData();
            setTimeout(() => this.$refs.form.resetValidation(), 1);
        },
        onAddNewLocation() {
            this.newLocation = {
                locationType: null,
                priority: 1,
                miningMethod: null,
                status: LocationStatus.Current
            };
        },
        cancelAddNewLocation() {
            this.newLocation = null;
        },
        select: waitFor('saving', async function() {
            if (!this.$refs.form.validate()) {
                return;
            }
            if (this.selectedLocations && this.selectedLocations.length) {
                this.$emit('selected', this.selectedLocations);
            }
            this.dialog = false;
        }),
        async saveAndAddNewLocation(newLocation) {
            this.$wait.start('saving');

            try{
                const locationResponse = await Locations.create({
                    ... newLocation,
                    departmentId: this.departmentId,
                    _type: "CreateLocationCommand",
                });

                if (locationResponse) {
                    this.$emit('selected', [locationResponse.id]);
                }
                this.dialog = false;
            } finally {
                this.$wait.end('saving');
            }
        },
        async saveAndAddNewStopeChild(newChildLocation){
            this.$wait.start('saving');

            try{
                const locationResponse = await Locations.createStopeSubLocation({
                    ...newChildLocation,
                    _type: 'CreateStopeSubLocationCommand'
                });

                if (locationResponse) {
                    this.$emit('selected', [newChildLocation.stopeId]);
                }
                this.dialog = false;
            } finally {
                this.$wait.end('saving');
            }
        },
        async loadData() {
            this.locations = await Locations.getByDepartment(this.departmentId);
            this.reducedLevels = await ReducedLevels.get();
            this.cycles = await Cycles.get();
        },
        toggle() {
            this.$nextTick(() => {
                if (this.allSelected) {
                    this.selectedLocations = [];
                } else {
                    this.selectedLocations = this.filteredLocations.map(x => x.id).slice();
                }
            });
        }
    },
    computed: {
        ...mapState(useMineAreaStore, ['locationNameConvention']),
        filteredLocations() {
            return this.locations
                .filter(x =>!this.existing.filter(e => e == x.id).length)
                .filter(x => x.parentLocationId == null)
                .filter(x => !this.locationFilter || x.name.toLowerCase().includes(this.locationFilter.toLowerCase()))
                .filter(x=>!x.isArchived)
                .sort((a, b) => {
                    if (a.displayOrder == b.displayOrder) {
                        if (a.name < b.name) {
                            return -1;
                        }
                        if (a.name > b.name) {
                            return 1;
                        }
                        return 0;
                    }
                    return a.displayOrder - b.displayOrder;
                });
        },
        filteredReducedLevels() {
            if (!this.reducedLevels || !this.mineAreaId) {
                return [];
            }
            var levels = this.reducedLevels
                .filter(x => x.mineAreaId == this.mineAreaId)
                .sort((a, b) => a.displayOrder - b.displayOrder);
            return levels;
        },
        filteredCycles() {
            if (!this.cycles || !this.departmentId) {
                return [];
            }
            return this.cycles.filter(x => x.departmentId == this.departmentId);
        },
        filteredCycleTypes() {
            if (!this.selectedDepartment) return [];
            return this.cycleTypes;
        },
        filteredOpenStopes() {
            return this.locations.filter(x=> x.miningMethod === MiningMethodType.SubLevelStoping && x.parentLocationId == null);
        },
        allSelected() {
            return this.selectedLocations.length === this.filteredLocations.length;
        },
        someSelected() {
            return this.selectedLocations.length > 0 && !this.allSelected;
        },
        icon() {
            if (this.allSelected) return 'mdi-checkbox-marked';
            if (this.someSelected) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        }
    }
};
</script>
<style lang="scss">
    .with-prepend {
        .prepend {
            position:sticky;
            top:0px;
            padding-top:8px;
            z-index: 1;
        }
        .v-select-list {
            padding-top: 0px;
        }
    }
</style>